import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';

/** Digits a NANP national number has once the mask's formatting is stripped. */
const NANP_DIGITS = 10;

/**
 * NANP area codes start 2-9: 0 reaches the operator and 1 is the long-distance prefix, so neither can
 * open a dialable number. Mirrors `PhoneValidation.IsNanpDialable` on the API and the rule the
 * activation wizard's details step applies, so a number accepted here is not rejected a screen later.
 *
 * The two faults are separate keys on purpose. `pattern` means the number cannot be dialled;
 * `incomplete` means it is merely unfinished. Reporting both as `pattern` is what told someone
 * halfway through typing 619... that their number starts with 0 or 1.
 */
export function nanpMobileValidator(control: AbstractControl): ValidationErrors | null {
  const digits = digitsOf(control.value);
  return digits ? faultOf(digits) : { required: true };
}

/**
 * The admin edit forms check only a number the admin changed. The one already on the record may not
 * pass today's rule (47 live suppliers hold French or placeholder numbers), and flagging it would block
 * an edit that never touched the phone. The API compares the same digits (UserController.UpdateUser).
 *
 * `stored` is the edited user's number, or null when adding one. FormService shares one form across
 * every open of the drawer, so set this each time it opens, or one user's number carries into the next.
 */
export function unchangedOrNanpMobileValidator(stored: string | null | undefined): ValidatorFn {
  const storedDigits = digitsOf(stored);
  return (control: AbstractControl): ValidationErrors | null => {
    const digits = digitsOf(control.value);
    return digits && digits !== storedDigits ? faultOf(digits) : null;
  };
}

/**
 * The same rule, but an empty field is valid. The admin user forms never require a mobile number:
 * the invitation goes out by email and the invitee enters their own number in the activation wizard,
 * which verifies it by SMS. An admin who does type one must still type a dialable one.
 */
export const optionalNanpMobileValidator: ValidatorFn = unchangedOrNanpMobileValidator(null);

/**
 * nz-form-control reveals a control's error as soon as it is dirty OR touched, and typing makes it
 * dirty on the very first digit - so a number was being called too short long before it could be
 * finished. Holding the field pristine until it is left moves the message to blur without weakening
 * the validator: it still runs on every keystroke, so once the message is out it disappears the
 * moment the number is good.
 *
 * Pair with {@link revealPhoneErrors} on the field's (blur). Unsubscribe in ngOnDestroy.
 */
export function deferPhoneErrorsUntilBlur(control: AbstractControl | null): Subscription | undefined {
  return control?.valueChanges.subscribe(() => {
    if (!control.touched) {
      control.markAsPristine();
    }
  });
}

/**
 * The field is no longer being typed, so whatever is in it can be judged. markAsTouched emits
 * nothing and nz-form-control only re-reads the control on statusChanges, hence the revalidation.
 */
export function revealPhoneErrors(control: AbstractControl | null): void {
  control?.markAsTouched();
  control?.updateValueAndValidity();
}

function digitsOf(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '');
}

function faultOf(digits: string): ValidationErrors | null {
  if (/^[01]/.test(digits)) {
    return { pattern: true };
  }
  return digits.length === NANP_DIGITS ? null : { incomplete: true };
}
